import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'

const routes = [
  {
    path: '/',
    name: 'public-home',
    component: () => import('../views/public/HomeView.vue'),
    // `/match/:idOrCode` è figlia della Home: HomeView resta montata e ospita
    // il dettaglio partita come pannello overlay (RouterView annidato). Così il
    // calendario non si smonta/rimonta aprendo un match → niente auto-scroll di
    // ritorno e posizione preservata. URL invariato (path figlio relativo).
    children: [
      {
        path: 'match/:idOrCode',
        name: 'public-match',
        component: () => import('../views/public/MatchDetailView.vue'),
        props: true
      }
    ]
  },
  {
    path: '/admin/login',
    name: 'admin-login',
    component: () => import('../views/admin/LoginView.vue')
  },
  {
    path: '/admin',
    component: () => import('../views/admin/AdminShell.vue'),
    meta: { requiresAuth: true },
    redirect: { name: 'admin-calendar' },
    children: [
      {
        path: 'settings',
        name: 'admin-settings',
        component: () => import('../views/admin/SettingsView.vue'),
        // Layout "trasparente": la view rende direttamente su sfondo lime,
        // niente white-card wrapping dal main di AdminShell. Le altre route
        // mantengono il white-card finché non vengono refattorizzate al nuovo
        // pattern (title fuori + box per gruppo).
        meta: { transparent: true }
      },
      {
        path: 'teams',
        name: 'admin-teams',
        component: () => import('../views/admin/TeamsView.vue'),
        meta: { transparent: true }
      },
      {
        path: 'calendar',
        name: 'admin-calendar',
        component: () => import('../views/admin/CalendarView.vue'),
        meta: { transparent: true }
      },
      {
        path: 'matches',
        name: 'admin-matches',
        component: () => import('../views/admin/MatchesView.vue'),
        meta: { transparent: true }
      },
      {
        path: 'final-phase',
        name: 'admin-final-phase',
        component: () => import('../views/admin/FinalPhaseView.vue'),
        meta: { transparent: true }
      },
      {
        path: 'extra',
        name: 'admin-extra',
        component: () => import('../views/admin/ExtraView.vue'),
        // Solo superadmin. Il backend rifiuta comunque le scritture, ma
        // mascheriamo anche la pagina lato client. `transparent` allinea il
        // layout al pattern Settings (title fuori + box per sezione).
        meta: { requiresRole: 'superadmin', transparent: true }
      },
      {
        path: 'stats',
        name: 'admin-stats',
        // Import dinamico → chunk lazy dedicato (ApexCharts finisce qui, NON
        // nel bundle pubblico/PWA). Accessibile a qualsiasi admin.
        component: () => import('../views/admin/StatsView.vue'),
        meta: { transparent: true }
      },
      {
        path: 'problem-log',
        name: 'admin-problem-log',
        // Solo superadmin: il guard beforeEach reindirizza gli admin non-super.
        // Lazy → chunk admin, fuori dal precache pubblico.
        component: () => import('../views/admin/ProblemLogView.vue'),
        meta: { requiresRole: 'superadmin', transparent: true }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Guard sessione: le rotte `/admin/*` (eccetto login) richiedono autenticazione.
// `to.meta` è il merge dei record matchati → eredita `requiresAuth` dal padre.
// `requiresRole` (es. 'superadmin') aggiunge un gate per ruolo: il backend è
// la fonte di verità, ma evitiamo di rendere una pagina vuota lato client.
router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) return true
  const auth = useAuthStore()
  if (!auth.checked) await auth.checkSession()
  if (!auth.authenticated) return { name: 'admin-login' }
  if (to.meta.requiresRole && auth.role !== to.meta.requiresRole) {
    return { name: 'admin-calendar' }
  }
  return true
})

export default router
